/**
 * Image Optimization Utilities
 * Manages lazy loading, aspect ratios, and responsive images
 */

/**
 * Generate responsive image srcset
 * @param {string} imagePath - Base image path
 * @param {number} maxWidth - Maximum image width
 * @returns {string} srcset string for responsive images
 */
export const generateImageSrcSet = (imagePath, maxWidth = 1200) => {
  if (!imagePath) return '';
  
  // For dynamic image URLs (Cloudinary, etc.)
  if (imagePath.includes('res.cloudinary.com') || imagePath.includes('cdn')) {
    return `
      ${imagePath}?w=400 400w,
      ${imagePath}?w=800 800w,
      ${imagePath}?w=1200 1200w
    `.trim();
  }

  return '';
};

/**
 * Preload critical images
 * @param {string[]} imagePaths - Array of image URLs to preload
 */
export const preloadImages = (imagePaths) => {
  imagePaths.forEach((path) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = path;
    document.head.appendChild(link);
  });
};

/**
 * Lazy load an image with fallback
 * @param {string} src - Image source
 * @param {string} placeholder - Placeholder image/color
 * @returns {Promise<string>} - Returns src when loaded
 */
export const lazyLoadImage = (src, placeholder = '#f3f4f6') => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(src);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
};

/**
 * Check if browser supports WebP
 * @returns {boolean}
 */
export const supportsWebP = () => {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 1;
  return canvas.toDataURL('image/webp').indexOf('webp') === 5;
};

/**
 * Get optimized image format
 * @param {string} imagePath - Original image path
 * @returns {string} - Optimized image path
 */
export const getOptimizedImagePath = (imagePath) => {
  if (!imagePath) return '';

  // If already WebP or optimized, return as is
  if (imagePath.includes('.webp')) {
    return imagePath;
  }

  // For Cloudinary or similar services, convert format
  if (imagePath.includes('res.cloudinary.com')) {
    if (supportsWebP()) {
      return imagePath.replace('/upload/', '/upload/f_webp,q_auto/');
    } else {
      return imagePath.replace('/upload/', '/upload/q_auto/');
    }
  }

  return imagePath;
};

/**
 * Calculate optimal dimensions for image
 * @param {number} width - Original width
 * @param {number} height - Original height
 * @param {number} containerWidth - Container width
 * @returns {object} - Optimized dimensions
 */
export const calculateOptimalDimensions = (width, height, containerWidth) => {
  if (!width || !height) return { width: containerWidth, height: containerWidth };
  
  const aspectRatio = width / height;
  const optimizedWidth = Math.min(containerWidth, width);
  const optimizedHeight = Math.round(optimizedWidth / aspectRatio);

  return {
    width: optimizedWidth,
    height: optimizedHeight,
  };
};
